/**
 * SettingsPasswordGate
 * Lightweight password re-confirmation flow for the Admin "Configurações" area.
 *
 * Exports:
 *   - isSettingsUnlocked()      → boolean
 *   - lockSettings()            → clears the unlock flag
 *   - SettingsPasswordDialog    → controlled dialog that verifies the admin password
 *
 * Unlock is stored in sessionStorage (cleared on tab close) under
 * the key `bilhetez_settings_unlocked`.
 */
import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, Loader2 } from "lucide-react";

import { API_URL } from "../../config";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";

const STORAGE_KEY = "bilhetez_settings_unlocked_at";
const TTL_MS = 30 * 60 * 1000; // 30 minutes

export const isSettingsUnlocked = () => {
  try {
    const ts = parseInt(sessionStorage.getItem(STORAGE_KEY) || "0", 10);
    if (!ts) return false;
    return Date.now() - ts < TTL_MS;
  } catch {
    return false;
  }
};

export const unlockSettings = () => {
  try {
    sessionStorage.setItem(STORAGE_KEY, String(Date.now()));
  } catch {
    /* noop */
  }
};

export const lockSettings = () => {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* noop */
  }
};

export const SettingsPasswordDialog = ({ open, onOpenChange, onSuccess, currentEmail }) => {
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setPassword("");
      setSubmitting(false);
    }
  }, [open]);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!password) {
      toast.error("Insira a password.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API_URL}/api/auth/elevate-config`,
        { config_password: password },
        { withCredentials: true }
      );
      unlockSettings();
      toast.success("Configurações desbloqueadas por 30 minutos");
      onOpenChange(false);
      onSuccess && onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Password incorrecta.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-zinc-900 border-white/10 text-white max-w-md"
        data-testid="settings-password-gate"
      >
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Lock className="w-5 h-5 text-amber-400" />
            Confirmar acesso a Configurações
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            {currentEmail ? (
              <>Insira a <strong>password de Configurações</strong> da conta <strong className="text-amber-300">{currentEmail}</strong> para aceder a esta área. Desbloqueia durante 30 minutos.</>
            ) : (
              <>Por segurança, insira a sua <strong>password de Configurações</strong> (definida pelo Master). Desbloqueia durante 30 minutos.</>
            )}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label className="text-zinc-300">Password</Label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="input-dark mt-2"
              autoFocus
              autoComplete="current-password"
              data-testid="settings-password-input"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              className="border-white/10 text-zinc-300"
              onClick={() => onOpenChange(false)}
              data-testid="settings-password-cancel"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={submitting || !password}
              className="btn-primary"
              data-testid="settings-password-submit"
            >
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {submitting ? "A verificar..." : "Desbloquear"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
